import { useTranslation } from "react-i18next";
import {
    TEXT_FIELD_TYPE,
    DATE_RANGE_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    BOOLEAN_FIELD_TYPE
} from "../../helpers/constants";
import { IconButton } from "@mui/material";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { useTransfersIndividualBillsFilteringData } from "../../services/bankingService";
import { TRANSFER_STATUS_KIND_PAID_CONNECTED } from "../../schemas/banking";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from '@mui/icons-material/Edit';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

import { MatchTransferWithAgreementDialog } from "./MatchTransferWithAgreementDialog";
import { formatCurrency } from "../../components/common/PriceDisplay";
import DateService from "../../services/dateService";
import { AgreementPassportButton } from "../AgreementsPage/AgreementPassportDialog/AgreementPassportButton";
import SelectFieldService from "../../services/selectFieldService";

export const TRANSFER_TABLE_NAME = "transfers_table";
export const TRANSFER_WITHDRAWAL_TABLE_NAME = "transfers_witdrawal_table";

export const useTransferTableFieldsConfig = (isWithdrawal) => {
    const { t } = useTranslation();

    const { data: filteringData } = useTransfersIndividualBillsFilteringData()

    const { openDialog: openTransferForAgreementDialog } = useDialogBetter();
    
    const handleClickOpenMatchTransferWithAgreementDialog = (e, transferId, agreementId) => {
    e.stopPropagation();
    e.preventDefault();
    openTransferForAgreementDialog({
        Component: MatchTransferWithAgreementDialog,
        componentProps: {
        agreementId: agreementId,
        transferId: transferId,
        readOnly:false,
        isWithdrawal: isWithdrawal,
        },
    });
    };

    return [
        {
            name: "transfer_payer",
            getValueCallback: (rowData) => rowData.transfer_payer,
            label: t("bankings.transfer_payer"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "created",
            getValueCallback: (rowData) => DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(rowData.created),
            label: t("bankings.transfer_created"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "transfer_date",
            getValueCallback: (rowData) => rowData.transfer_date,
            label: t("bankings.transfer_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "transfer_title",
            getValueCallback: (rowData) => rowData.transfer_title,
            label: t("bankings.transfer_title"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "transfer_payer_iban",
            getValueCallback: (rowData) => rowData.transfer_payer_iban,
            label: t("bankings.transfer_payer_iban"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
            {
            name: "transfer_reciver_iban",
            getValueCallback: (rowData) => rowData.transfer_reciver_iban,
            label: t("bankings.transfer_reciver_iban"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "transfer_amount",
            getValueCallback: (rowData) => formatCurrency(rowData.transfer_amount),
            label: t("bankings.transfer_amount"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "transfer_agreement_nr",
            getValueCallback: (rowData) => rowData.transfer_agreement_nr,
            label: t("bankings.transfer_agreement_nr"),
            filterType: TEXT_FIELD_TYPE,
            // sortable: true
        },
        {
            name: "agreement_notarial_deed",
            getValueCallback: (rowData) => rowData.agreement_notarial_deed,
            label: t("bankings.agreement_notarial_deed"),
            filterType: TEXT_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "agreement_customer",
            getValueCallback: (rowData) => (
                rowData.agreement_customer && rowData.agreement_customer_has_multiple_agreements ? 
                <>
                    {rowData.agreement_customer}
                    <InfoIcon color="primary" fontSize="small"/>
                </> : 
                rowData.agreement_customer
            ),
            label: t("bankings.agreement_customer"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "agreement_company",
            getValueCallback: (rowData) => (
                rowData.agreement_company && rowData.agreement_customer_has_multiple_agreements ? 
                <>
                    {rowData.agreement_company}
                    <InfoIcon color="primary" fontSize="small" />
                </> : 
                rowData.agreement_company
            ),
            label: t("bankings.agreement_company"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "agreement_product",
            getValueCallback: (rowData) => rowData.agreement_product,
            label: t("bankings.agreement_product"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "transfer_status",
            getValueCallback: (rowData) => rowData.transfer_status_name,
            other_export_field_name: "transfer_status_name",
            label: t("bankings.transfer_status_name"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                optionLabelKey: "transfer_status_name",
                options: filteringData?.transfer_status,
                multiple: true,
                isObjectOption: true,
                disableClearable: true
            },
            sortable: true,
        },
        {
            name: "transfer_is_booked",
            getValueCallback: (rowData) =>  SelectFieldService.getLabelOptionsTrueOrFalse(rowData.transfer_is_booked),
            label: t("bankings.transfer_is_booked"),
            filterType: BOOLEAN_FIELD_TYPE,
            sortable: false,
        },
        {
            name: "has_invoice",
            getValueCallback: (rowData) => SelectFieldService.getLabelOptionsYesOrNo(rowData.has_invoice),
            label: t("bankings.has_invoice"),
            filterType: BOOLEAN_FIELD_TYPE,
        },
        {
            name: "edit",
            getValueCallback: (rowData) => (
                rowData.transfer_status_id === TRANSFER_STATUS_KIND_PAID_CONNECTED.id ? 
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleClickOpenMatchTransferWithAgreementDialog(e, rowData.id, rowData.transfer_agreement_id)}
                >
                <EditIcon />
                </IconButton>
                : 
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleClickOpenMatchTransferWithAgreementDialog(e, rowData.id, rowData.transfer_agreement_id)}
                >
                    <PersonSearchIcon />
                </IconButton>
            ),
            allowChangeVisibility: false,
            excludedFromExport: true,
        },
        {
            name: "passport",
            getValueCallback: (rowData) => rowData.transfer_agreement_id ? <AgreementPassportButton agreement={{id:rowData.transfer_agreement_id}} /> : null,
            label: t("bankings.agreement_passport"),
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
        },
    ];
};
